import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { usePrefetch } from '../../redux/thunks/users/userprofile';
import EmptyBusinessNetworkState from './EmptyBusinessNetworkState';

import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Avatar,
  Chip,
  Skeleton,
  IconButton,
  InputAdornment,
  Divider,
  Card,
  CardContent,
  Popover,
  MenuList,
  MenuItem,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Drawer,
  AppBar,
  Toolbar,
  Fab,
  SwipeableDrawer,
  Collapse,
  Badge
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  ImportContacts as ImportIcon,
  Share as ShareIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  Info as InfoIcon,
  Analytics as StatsIcon,
  Assignment as TasksIcon,
  Lightbulb as SuggestionsIcon,
  PersonAdd as PersonAddIcon,
  TuneOutlined as TuneIcon
} from '@mui/icons-material';

import { useFindUserMutation } from '../../redux/thunks/users/userfind';
import { useEmailInviteMutation } from '../../redux/thunks/users/useremailinvite';
import { useAddTrustedUserMutation } from '../../redux/thunks/users/useraddtrusteduser';
import { useGetUserIncomingReqQuery } from '../../redux/thunks/users/userincomingrequests';
import { useGetUserOutgoingReqQuery } from '../../redux/thunks/users/useroutgoingrequests';
import { useGetNetworkStatsQuery } from '../../redux/thunks/users/getnetworkstats';
import { useGetUserSuggestionsQuery } from '../../redux/thunks/users/getusersuggestions';
import { useGetConnectedBusinessesQuery } from '../../redux/thunks/users/getconnectedbusiness';

const TRUST_SCORE_RANGES = [
  { label: 'All Scores', value: 'all' },
  { label: '90+ (Excellent)', value: '90' },
  { label: '80-89 (Very Good)', value: '80' },
  { label: '70-79 (Good)', value: '70' }
];

const GROWTH_TIPS = [
  'Personalize your invitations',
  'Connect with mutual contacts',
  'Build your trust score',
  'Stay active in your network'
];

// All existing skeleton components remain exactly the same
const StatCardSkeleton = () => (
  <Paper variant='outlined' sx={{ p: 3, mb: 2, border: 'none' }}>
    <Skeleton variant="text" width="60%" height={32} sx={{ mb: 2 }} />
    <Grid container spacing={2}>
      {[1, 2, 3, 4].map((item) => (
        <Grid item xs={6} key={item}>
          <Skeleton variant="text" width="60%" height={40} />
          <Skeleton variant="text" width="80%" height={24} />
        </Grid>
      ))}
    </Grid>
  </Paper>
);

const SearchResultSkeleton = () => (
  <Paper variant='outlined' sx={{ p: 2, mb: 2, border: 'none' }}>
    <Box sx={{ display: 'flex', gap: 2 }}>
      <Skeleton variant="circular" width={50} height={50} />
      <Box sx={{ flex: 1 }}>
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="text" width="40%" />
        <Skeleton variant="text" width="30%" />
      </Box>
      <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: 18 }} />
    </Box>
  </Paper>
);

// 🆕 Mobile Search Sidebar Component
const MobileSearchSidebar = ({ 
  isOpen, 
  onClose,
  networkStatsData,
  suggestionsData,
  onConnect,
  navigate
}) => {
  const [expandedSections, setExpandedSections] = useState({
    stats: true,
    actions: false,
    invite: false,
    recommended: false,
    tips: false
  });

  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState(null);
  const [inviteUser, { isLoading: inviteLoading }] = useEmailInviteMutation();

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleEmailInvite = async () => {
    try {
      setError(null);
      const payload = {
        emails: [email],
        message,
      };
      const response = await inviteUser(payload).unwrap();
      setEmail('');
      setMessage('');
    } catch (err) {
      console.error('Error sending invitation:', err);
      setError(err?.data?.message || 'An unexpected error occurred.');
    }
  };

  return (
    <SwipeableDrawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      onOpen={() => {}}
      PaperProps={{
        sx: { 
          width: '100%',
          maxWidth: 400,
          bgcolor: 'background.default'
        }
      }}
    >
      <AppBar position="static" color="default" elevation={0}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Search Tools
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 2, overflow: 'auto', height: '100%' }}>
        {/* Network Stats Section */}
        <Card sx={{ mb: 2 }}>
          <CardContent 
            sx={{ 
              pb: 1, 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
            onClick={() => toggleSection('stats')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <StatsIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Network Stats</Typography>
            </Box>
            {expandedSections.stats ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </CardContent>
          <Collapse in={expandedSections.stats}>
            <CardContent sx={{ pt: 0 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 1 }}>
                    <Typography variant="h6" color="primary">
                      {networkStatsData?.networkFollows || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Follows
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 1 }}>
                    <Typography variant="h6" color="primary">
                      {networkStatsData?.networkReferred || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Referred
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 1 }}>
                    <Typography variant="h6" color="primary">
                      {networkStatsData?.networkReviews || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Reviews
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 1 }}>
                    <Typography variant="h6" color="primary">
                      {networkStatsData?.networkViews || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Pages Viewed
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Collapse>
        </Card>

        {/* Quick Actions Section */}
        <Card sx={{ mb: 2 }}>
          <CardContent 
            sx={{ 
              pb: 1, 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
            onClick={() => toggleSection('actions')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <TasksIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Quick Actions</Typography>
            </Box>
            {expandedSections.actions ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </CardContent>
          <Collapse in={expandedSections.actions}>
            <CardContent sx={{ pt: 0 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button 
                  variant="contained" 
                  startIcon={<ImportIcon />}
                  size="large"
                >
                  Import Email Contacts
                </Button>
                <Button 
                  variant="outlined" 
                  startIcon={<ShareIcon />}
                  size="large"
                >
                  Share Profile Link
                </Button>
              </Box>
            </CardContent>
          </Collapse>
        </Card>

        {/* Email Invite Section */}
        <Card sx={{ mb: 2 }}>
          <CardContent 
            sx={{ 
              pb: 1, 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
            onClick={() => toggleSection('invite')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <EmailIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Invite via Email</Typography>
            </Box>
            {expandedSections.invite ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </CardContent>
          <Collapse in={expandedSections.invite}>
            <CardContent sx={{ pt: 0 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  placeholder="Enter email address"
                  size="small"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Add a personal message..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
                {error && (
                  <Typography color="error" variant="body2">
                    {error}
                  </Typography>
                )}
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleEmailInvite}
                  disabled={inviteLoading || !email}
                  size="large"
                >
                  {inviteLoading ? <CircularProgress size={24} /> : 'Send Invite'}
                </Button>
              </Box>
            </CardContent>
          </Collapse>
        </Card>

        {/* Recommended Users Section */}
        {suggestionsData && suggestionsData.length > 0 && (
          <Card sx={{ mb: 2 }}>
            <CardContent 
              sx={{ 
                pb: 1, 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
              onClick={() => toggleSection('recommended')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PersonAddIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Recommended</Typography>
                <Badge badgeContent={suggestionsData.length} color="primary" sx={{ ml: 1 }} />
              </Box>
              {expandedSections.recommended ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </CardContent>
            <Collapse in={expandedSections.recommended}>
              <CardContent sx={{ pt: 0 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {suggestionsData.map((user) => (
                    <Box
                      key={user.id}
                      sx={{
                        display: 'flex',
                        gap: 2,
                        p: 2,
                        bgcolor: 'grey.50',
                        borderRadius: 1,
                        alignItems: 'center',
                        cursor: 'pointer',
                        '&:hover': { 
                          bgcolor: 'grey.100',
                        }
                      }}
                      onClick={() => {
                        navigate(`/profile/${user._id}/active`);
                        onClose();
                      }}
                    >
                      <Avatar 
                        src={user.avatar || user.profileImageUrl} 
                        sx={{ cursor: 'pointer' }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography 
                          variant="subtitle2"
                          sx={{ 
                            '&:hover': { 
                              textDecoration: 'underline',
                              color: 'primary.main'
                            }
                          }}
                        >
                          {user.name || user.displayName}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Collapse>
          </Card>
        )}

        {/* Growth Tips Section */}
        <Card sx={{ mb: 2 }}>
          <CardContent 
            sx={{ 
              pb: 1, 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
            onClick={() => toggleSection('tips')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <SuggestionsIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Growth Tips</Typography>
            </Box>
            {expandedSections.tips ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </CardContent>
          <Collapse in={expandedSections.tips}>
            <CardContent sx={{ pt: 0 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {GROWTH_TIPS.map((tip, index) => (
                  <Box key={index} sx={{ 
                    p: 1.5,
                    bgcolor: 'info.light',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'info.main'
                  }}>
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                      💡 {tip}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Collapse>
        </Card>
      </Box>
    </SwipeableDrawer>
  );
};

// 🆕 Mobile Search Banner
const MobileSearchBanner = ({ searchQuery, onSearchSubmit, onSearchChange, onKeyPress, searchInitiated, onClearSearch }) => {
  return (
    <Box 
      sx={{ 
        bgcolor: 'primary.main', 
        color: 'white',
        p: 2,
        mb: 3 
      }}
    >
      <Container maxWidth="xl">
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          Find & Invite
        </Typography>
        <form onSubmit={onSearchSubmit}>
          <TextField
            fullWidth
            placeholder="Search by name, company, or role..."
            value={searchQuery}
            onChange={onSearchChange}
            onKeyDown={onKeyPress}
            size="medium"
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'white',
                '& fieldset': { borderColor: 'transparent' },
                '&:hover fieldset': { borderColor: 'transparent' },
                '&.Mui-focused fieldset': { borderColor: 'primary.main' },
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchInitiated && (
                <InputAdornment position="end">
                  <Button 
                    size="small" 
                    onClick={onClearSearch}
                    sx={{ mr: 1, color: 'text.secondary' }}
                  >
                    Clear
                  </Button>
                </InputAdornment>
              ),
            }}
          />
        </form>
      </Container>
    </Box>
  );
};

// All existing component functions remain exactly the same
const NetworkStatsCard = ({ stats }) => (
  <Paper variant='outlined' sx={{ p: 3, mb: 2, border: 'none' }}>
    <Typography variant="h6" gutterBottom>Network Statistics</Typography>
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <Typography variant="h4" color="primary">{stats.networkFollows || 0 }</Typography>
        <Typography variant="body2" color="text.secondary">Follows</Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="h4" color="primary">{stats.networkReferred || 0}</Typography>
        <Typography variant="body2" color="text.secondary">Referred</Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="h4" color="primary">{stats.networkReviews || 0}</Typography>
        <Typography variant="body2" color="text.secondary">Reviews</Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="h4" color="primary">{stats.networkViews || 0}</Typography>
        <Typography variant="body2" color="text.secondary">Pages Viewed</Typography>
      </Grid>
    </Grid>
  </Paper>
);

const QuickActionsCard = () => (
  <Paper variant='outlined' sx={{ p: 3, mb: 2, border: 'none' }}>
    <Typography variant="h6" gutterBottom>Quick Actions</Typography>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Button variant="contained" startIcon={<ImportIcon />}>
        Import Email Contacts
      </Button>
      <Button variant="outlined" startIcon={<ShareIcon />}>
        Share Profile Link
      </Button>
    </Box>
  </Paper>
);

const GrowthTipsCard = ({ tips }) => (
  <Paper variant='outlined' sx={{ p: 3, border: 'none' }}>
    <Typography variant="h6" gutterBottom>Network Growth Tips</Typography>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {tips.map((tip, index) => (
        <Typography key={index} variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
          • {tip}
        </Typography>
      ))}
    </Box>
  </Paper>
);

const SearchResultCard = ({ user, onConnect, thisUser }) => {
  const navigate = useNavigate();

  const [isPending, setIsPending] = useState(false);
  const [showRequestSent, setShowRequestSent] = useState(false);
  
  const firstDegreeFriends = thisUser.firstDegreeFriends || []
  
  const prefetchUserProfiles = usePrefetch('getUserProfile')
  const { data: incoming, isLoading: incomingLoading, isSuccess: incomingSuccess  } = useGetUserIncomingReqQuery(thisUser._id);
  const { data: outgoing, isLoading: outgoingLoading, isSuccess: outgoingSuccess  } = useGetUserOutgoingReqQuery(thisUser._id);
  
  const isConnected = firstDegreeFriends && firstDegreeFriends.length > 0 && 
    firstDegreeFriends.some((friend) => 
      friend.userId && user._id && friend.userId.toString() === user._id.toString());
    
  const isMe = thisUser._id === user._id
  const isLoading = incomingLoading || outgoingLoading

  const handleConnect = async (userId) => {
    setShowRequestSent(true);
    setTimeout(() => {
      setShowRequestSent(false);
      setIsPending(true);
    }, 2000);
    
    onConnect(userId);
  };
  
  const goToUserProfile = () => {
    navigate(`/profile/${user._id}/active`);
  };
  
  const goToBusinessPage = (e) => {
    e.stopPropagation();
    if (user.businessId && user.businessName) {
      const formattedBusinessName = user.businessName.replace(/[\s,\.]+/g, '-').toLowerCase();
      navigate(`/business/${user.businessId}/${formattedBusinessName}`);
    }
  };

  useEffect(() => {
    if (incoming && outgoing) {
      const incomingIds = incoming.pendingInTrustedUsers.map(item => item.trustedUserId);
      const outgoingIds = outgoing.pendingOutTrustedUsers.map(item => item.trustedUserId);
      const isPendingIn = incoming.pendingInTrustedUsers.length > 0 && 
        incoming.pendingInTrustedUsers.some((friend) => 
          friend.trustedUserId.toString() === user._id.toString());
      const isPendingOut = outgoing.pendingOutTrustedUsers.length > 0 && 
        outgoing.pendingOutTrustedUsers.some((friend) => 
          friend.trustedUserId.toString() === user._id.toString());
      setIsPending(isPendingIn || isPendingOut)
      const profileIds = [...new Set([...incomingIds, ...outgoingIds])];
    }
  }, [incoming, outgoing, user]);

  return (
    <Paper variant='outlined' sx={{ p: 2, mb: 2, border: 'none' }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <Avatar 
          src={user.profileImageUrl} 
          sx={{ 
            width: 50, 
            height: 50,
            cursor: 'pointer'
          }}
          onClick={goToUserProfile}
        />
        <Box sx={{ flex: 1 }}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              cursor: 'pointer',
              '&:hover': { 
                textDecoration: 'underline',
                color: 'primary.main'
              }
            }}
            onClick={goToUserProfile}
          >
            {user.displayName}
          </Typography>
          
          {user.businessName && (
            <Typography variant="body2" color="text.secondary">
              {user.role} at{' '}
              <Box
                component="span"
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': { 
                    textDecoration: 'underline',
                    color: 'primary.main'
                  }
                }}
                onClick={goToBusinessPage}
              >
                {user.businessName}
              </Box>
            </Typography>
          )}
          
          <Typography variant="body2" color="text.secondary">
            {user.mutualConnections} mutual connections
          </Typography>
          
          {showRequestSent && (
            <Typography variant="body2" color="success.main" sx={{ mt: 1, fontWeight: 'bold' }}>
              Connection request sent!
            </Typography>
          )}
        </Box>
        
        {!isConnected && !isMe && !isPending && !showRequestSent &&
          <Button 
            disabled={isLoading} 
            variant="outlined" 
            sx={{ borderRadius: '999px', border: '1px solid', padding: '8px 16px' }}  
            onClick={() => handleConnect(user._id)}
          >
            {isLoading ? 'Connecting...' : 'Connect'}
          </Button>      
        }
        
        {isConnected &&
          <Button 
            variant="outlined" 
            sx={{ borderRadius: '999px', border: '1px solid', padding: '8px 16px' }}
          >
            Message
          </Button>      
        }
        
        {isMe &&
          <Button 
            variant="outlined" 
            sx={{ borderRadius: '999px', border: '1px solid', padding: '8px 16px' }}
          >
            You
          </Button>      
        }
        
        {(isPending || showRequestSent) &&
          <Button 
            variant="outlined" 
            sx={{ 
              borderRadius: '999px', 
              border: '1px solid', 
              padding: '8px 16px',
              bgcolor: showRequestSent ? 'success.light' : 'inherit',
              color: showRequestSent ? 'white' : 'inherit'
            }}
          >
            {showRequestSent ? 'Sent!' : 'Pending'}
          </Button>      
        }
      </Box>
    </Paper>
  );
};

const EmailInviteCard = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState(null);
  const [inviteUser, { isLoading }] = useEmailInviteMutation();

  const handleSubmit = async () => {
    try {
      setError(null);
      const payload = {
        emails: [email],
        message,
      };
      const response = await inviteUser(payload).unwrap();
      console.log('Invitation sent successfully:', response);
      setEmail('');
      setMessage('');
    } catch (err) {
      console.error('Error sending invitation:', err);
      setError(err?.data?.message || 'An unexpected error occurred.');
    }
  };
  
  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 2, border: 'none' }}>
      <Typography variant="h6" gutterBottom>
        Invite via Email
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          fullWidth
          placeholder="Enter email address"
          size="small"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EmailIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          fullWidth
          multiline
          rows={4}
          placeholder="Add a personal message..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}
        <Button
          variant="contained"
          fullWidth
          onClick={handleSubmit}
          disabled={isLoading || !email}
        >
          {isLoading ? <CircularProgress size={24} /> : 'Send Invite'}
        </Button>
      </Box>
    </Paper>
  );
};

const RecommendedUsersCard = ({ users, onConnect }) => { 
  const navigate = useNavigate();

  const goToUserProfile = (userId) => {
    navigate(`/profile/${userId}/active`);
  };
  
  return (
    <Paper variant='outlined' sx={{ p: 3, border: 'none' }}>
      <Typography variant="h6" gutterBottom>Recommended</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {users && users.map((user) => (
          <Box
            key={user.id}
            sx={{
              display: 'flex',
              gap: 2,
              p: 2,
              bgcolor: 'grey.50',
              borderRadius: 1,
              alignItems: 'center',
              cursor: 'pointer',
              '&:hover': { 
                bgcolor: 'grey.100',
              }
            }}
            onClick={() => goToUserProfile(user._id)}
          >
            <Avatar 
              src={user.avatar || user.profileImageUrl} 
              sx={{ cursor: 'pointer' }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography 
                variant="subtitle2"
                sx={{ 
                  '&:hover': { 
                    textDecoration: 'underline',
                    color: 'primary.main'
                  }
                }}
              >
                {user.name || user.displayName}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

// 🆕 Enhanced Main Component with Mobile Support
export default function SearchInvitePage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Mobile-specific state
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const thisUser = useSelector((state) => state.auth?.user || '');

  const [searchQuery, setSearchQuery] = useState('');
  const [searchInitiated, setSearchInitiated] = useState(false);
  const [displayedUsers, setDisplayedUsers] = useState([]);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [showSameRegion, setShowSameRegion] = useState(false);
  const [selectedTrustScore, setSelectedTrustScore] = useState('all');
  const [anchorElTrust, setAnchorElTrust] = useState(null);

  const { data: networkStatsData, isLoading: isNetworkStatsDataLoading } = useGetNetworkStatsQuery();
  const { data: suggestionsData, isLoading: isSuggestionsDataLoading } = useGetUserSuggestionsQuery();
  const { data: connectedBusinessData, isLoading: isConnectedBusinessDataLoading } = useGetConnectedBusinessesQuery();

  const [triggerSearch, { data: searchData, isLoading: isSearchLoading }] = useFindUserMutation();
  const [addTrustedUser, { isLoading: isConnectLoading }] = useAddTrustedUserMutation();

  // Calculate paginated users
  const paginatedUsers = displayedUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // All existing useEffect hooks and handler functions remain exactly the same
  useEffect(() => {
    if (connectedBusinessData && !searchInitiated) {
      const allBusinesses = [];
      
      if (Array.isArray(connectedBusinessData)) {
        connectedBusinessData.forEach(networkUser => {
          if (networkUser.businesses && Array.isArray(networkUser.businesses)) {
            networkUser.businesses.forEach(business => {
              if (business.spDisplayName === "Revfer system" || business.spUserId === null) {
                return;
              }

              allBusinesses.push({
                _id: business.spUserId,
                businessId: business.businessId,
                displayName: business.spDisplayName || 'Service Provider',
                role: "Service Provider",
                businessName: business.businessName,
                mutualConnections: 1,
                trustScore: 80,
                profileImageUrl: business.spPhoto,
                isPopular: true,
                region: thisUser.region,
                connectedThrough: networkUser.displayName
              });
            });
          }
        });
      } 
      else if (connectedBusinessData.businesses && Array.isArray(connectedBusinessData.businesses)) {
        connectedBusinessData.businesses.forEach(business => {
          if (business.spDisplayName === "Revfer system" || business.spUserId === null) {
            return;
          }

          allBusinesses.push({
            _id: business.spUserId,
            businessId: business.businessId,
            displayName: business.spDisplayName || 'Service Provider',
            role: "Service Provider",
            businessName: business.businessName,
            mutualConnections: 0,
            trustScore: 80,
            profileImageUrl: business.spPhoto,
            isPopular: true,
            region: thisUser.region
          });
        });
      }
      
      const uniqueBusinesses = [...new Map(allBusinesses.map(item => 
        [item._id, item])).values()];
      
      setDisplayedUsers(uniqueBusinesses);
      setPage(0);
    }
  }, [connectedBusinessData, searchInitiated, thisUser.region]);

  useEffect(() => {
    if (searchData) {
      setDisplayedUsers(searchData);
      setPage(0);
    }
  }, [searchData]);

  const applyFilters = () => {
    let filteredUsers = [];
    
    if (searchInitiated && searchData) {
      filteredUsers = [...searchData];
    } else if (connectedBusinessData) {
      filteredUsers = buildUsersFromConnectedBusinesses();
    }

    if (searchQuery && !searchInitiated) {
      filteredUsers = filteredUsers.filter(user => 
        user.displayName.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (showSameRegion) {
      filteredUsers = filteredUsers.filter(user => user.region === thisUser.region);
    }

    if (selectedTrustScore !== 'all') {
      const minScore = parseInt(selectedTrustScore);
      filteredUsers = filteredUsers.filter(user => user.trustScore >= minScore);
    }

    setDisplayedUsers(filteredUsers);
    setPage(0);
  };

  const buildUsersFromConnectedBusinesses = () => {
    const allBusinesses = [];
    
    if (Array.isArray(connectedBusinessData)) {
      connectedBusinessData.forEach(networkUser => {
        if (networkUser.businesses && Array.isArray(networkUser.businesses)) {
          networkUser.businesses.forEach(business => {
            if (business.spDisplayName === "Revfer system" || business.spUserId === null) {
              return;
            }
            allBusinesses.push({
              _id: business.spUserId,
              businessId: business.businessId,
              displayName: business.spDisplayName || 'Service Provider',
              role: "Service Provider",
              businessName: business.businessName,
              mutualConnections: 1,
              trustScore: 80,
              profileImageUrl: business.spPhoto,
              isPopular: true,
              region: thisUser.region,
              connectedThrough: networkUser.displayName
            });
          });
        }
      });
    } else if (connectedBusinessData.businesses && Array.isArray(connectedBusinessData.businesses)) {
      connectedBusinessData.businesses.forEach(business => {
        if (business.spDisplayName === "Revfer system" || business.spUserId === null) {
          return;
        }
        allBusinesses.push({
          _id: business.spUserId,
          businessId: business.businessId,
          displayName: business.spDisplayName || 'Service Provider',
          role: "Service Provider",
          businessName: business.businessName,
          mutualConnections: 0,
          trustScore: 80,
          profileImageUrl: business.spPhoto,
          isPopular: true,
          region: thisUser.region
        });
      });
    }
    
    return [...new Map(allBusinesses.map(item => [item._id, item])).values()];
  };

  const handleTrustClick = (event) => {
    setAnchorElTrust(event.currentTarget);
  };
  
  const handleTrustClose = () => {
    setAnchorElTrust(null);
  };
  
  const handleTrustSelect = (score) => {
    setSelectedTrustScore(score);
    handleTrustClose();
    applyFilters();
  };

  const handleConnect = async (userId) => {
    try {
      const payload = { 'trustedUserId': userId, requestType: 'new' }
      await addTrustedUser({payload, userId: userId});
    } catch (error) {
      console.error('Failed to add trusted user:', error);
    }
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleSearchSubmit = async(event) => {
    event.preventDefault();
    if (searchQuery.trim()) {
      setSearchInitiated(true);
      const payload = { text: searchQuery };
      await triggerSearch({ payload });
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSearchSubmit(event);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchInitiated(false);
    
    setShowSameRegion(false);
    setSelectedTrustScore('all');
    setPage(0);
    
    if (connectedBusinessData) {
      setDisplayedUsers(buildUsersFromConnectedBusinesses());
    } else {
      setDisplayedUsers([]);
    }
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };
  
  const handlePrevPage = () => {
    setPage(Math.max(0, page - 1));
  };
  
  const handleNextPage = () => {
    setPage(Math.min(Math.ceil(displayedUsers.length / rowsPerPage) - 1, page + 1));
  };

  const isPageLoading = isNetworkStatsDataLoading || isConnectedBusinessDataLoading || isSuggestionsDataLoading;

  if (isPageLoading) {
    return (
      <Box>
        <Box 
          sx={{ 
            bgcolor: 'primary.main', 
            color: 'white',
            p: 3,
            mb: 3 
          }}
        >
          <Container maxWidth="xl">
            <Skeleton 
              variant="text" 
              width={300} 
              height={40} 
              sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} 
            />
          </Container>
        </Box>

        <Container maxWidth="xl">
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <StatCardSkeleton />
              <StatCardSkeleton />
              <StatCardSkeleton />
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper variant='outlined' sx={{ p: 3, mb: 2, border: 'none' }}>
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
              </Paper>
              {[1, 2, 3].map((item) => (
                <SearchResultSkeleton key={item} />
              ))}
            </Grid>

            <Grid item xs={12} md={3}>
              <StatCardSkeleton />
              <StatCardSkeleton />
            </Grid>
          </Grid>
        </Container>
      </Box>
    );
  }

  return (
    <Box>
      {/* Search Banner - Responsive */}
      {isMobile ? (
        <MobileSearchBanner 
          searchQuery={searchQuery}
          onSearchSubmit={handleSearchSubmit}
          onSearchChange={handleSearch}
          onKeyPress={handleKeyPress}
          searchInitiated={searchInitiated}
          onClearSearch={clearSearch}
        />
      ) : (
        <Box 
          sx={{ 
            bgcolor: 'primary.main', 
            color: 'white',
            p: 3,
            mb: 3 
          }}
        >
          <Container maxWidth="xl">
            <Typography variant="h4">Find & Invite Connections</Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
              Grow your trusted network
            </Typography>
          </Container>
        </Box>
      )}

      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
        {isMobile ? (
          // Mobile Layout - Single Column
          <>
            {/* Mobile Header with Filters and Menu */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              mb: 3
            }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', flex: 1 }}>
                <Chip
                  label={`Region ${showSameRegion ? '• On' : '• Off'}`}
                  variant="outlined"
                  onClick={() => {
                    setShowSameRegion(!showSameRegion);
                    setTimeout(applyFilters, 0);
                  }}
                  color={showSameRegion ? "primary" : "default"}
                  size="small"
                />
                <Chip
                  label={`Trust ${selectedTrustScore !== 'all' ? `• ${selectedTrustScore}+` : '▼'}`}
                  variant="outlined"
                  onClick={handleTrustClick}
                  color={selectedTrustScore !== 'all' ? "primary" : "default"}
                  size="small"
                />
                
                <Popover
                  open={Boolean(anchorElTrust)}
                  anchorEl={anchorElTrust}
                  onClose={handleTrustClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                >
                  <MenuList>
                    {TRUST_SCORE_RANGES.map((range) => (
                      <MenuItem 
                        key={range.value}
                        onClick={() => handleTrustSelect(range.value)}
                        selected={selectedTrustScore === range.value}
                      >
                        {range.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Popover>
              </Box>

              <IconButton 
                color="primary" 
                onClick={() => setSidebarOpen(true)}
                sx={{ 
                  bgcolor: 'primary.light',
                  '&:hover': { bgcolor: 'primary.main', color: 'white' }
                }}
              >
                <MenuIcon />
              </IconButton>
            </Box>

            {/* Mobile Content - Full Width */}
            {!searchInitiated && displayedUsers.length === 0 && !isSearchLoading ? (
              <EmptyBusinessNetworkState 
                onSearchClick={() => {
                  const searchInput = document.querySelector('input[placeholder*="Search by name"]');
                  if (searchInput) {
                    searchInput.focus();
                  }
                }}
              />
            ) : (
              <>
                <Box sx={{ px: 1, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                  <Box>
                    <Typography variant="h6">
                      {searchInitiated ? 'Search Results' : 'Extended Business Network'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {searchInitiated 
                        ? `Found ${displayedUsers.length} results for "${searchQuery}"`
                        : 'Service providers from businesses in your extended network'}
                    </Typography>
                  </Box>
                  
                  {displayedUsers.length > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, displayedUsers.length)} of {displayedUsers.length}
                    </Typography>
                  )}
                </Box>
                
                {isSearchLoading ? (
                  [1, 2, 3].map((item) => (
                    <SearchResultSkeleton key={item} />
                  ))
                ) : displayedUsers.length > 0 ? (
                  <>
                    {paginatedUsers.map((user) => (
                      <SearchResultCard
                        key={user._id}
                        thisUser={thisUser}
                        user={user}
                        onConnect={handleConnect}
                      />
                    ))}
                    
                    {displayedUsers.length > rowsPerPage && (
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        my: 3,
                        gap: 1,
                        flexWrap: 'wrap'
                      }}>
                        <Button 
                          variant="outlined" 
                          disabled={page === 0}
                          onClick={handlePrevPage}
                          size="small"
                        >
                          Previous
                        </Button>
                        
                        {[...Array(Math.min(3, Math.ceil(displayedUsers.length / rowsPerPage)))].map((_, i) => {
                          let pageNum;
                          const totalPages = Math.ceil(displayedUsers.length / rowsPerPage);
                          
                          if (totalPages <= 3) {
                            pageNum = i;
                          } else if (page < 2) {
                            pageNum = i;
                          } else if (page >= totalPages - 2) {
                            pageNum = totalPages - 3 + i;
                          } else {
                            pageNum = page - 1 + i;
                          }
                          
                          return (
                            <Button 
                              key={pageNum}
                              variant={page === pageNum ? "contained" : "outlined"}
                              onClick={() => handleChangePage(pageNum)}
                              size="small"
                              sx={{ minWidth: '40px' }}
                            >
                              {pageNum + 1}
                            </Button>
                          );
                        })}
                        
                        <Button 
                          variant="outlined" 
                          disabled={page >= Math.ceil(displayedUsers.length / rowsPerPage) - 1}
                          onClick={handleNextPage}
                          size="small"
                        >
                          Next
                        </Button>
                      </Box>
                    )}
                  </>
                ) : (
                  <Paper variant='outlined' sx={{ p: 3, textAlign: 'center', border: 'none' }}>
                    <Typography variant="body1">No results found</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Try adjusting your search criteria
                    </Typography>
                  </Paper>
                )}
              </>
            )}

            {/* Mobile Floating Action Button */}
            <Fab
              color="primary"
              sx={{ 
                position: 'fixed', 
                bottom: 16, 
                right: 16,
                zIndex: 1000
              }}
              onClick={() => setSidebarOpen(true)}
            >
              <TuneIcon />
            </Fab>

            {/* Mobile Search Sidebar */}
            <MobileSearchSidebar
              isOpen={sidebarOpen}
              onClose={() => setSidebarOpen(false)}
              networkStatsData={networkStatsData}
              suggestionsData={suggestionsData}
              onConnect={handleConnect}
              navigate={navigate}
            />
          </>
        ) : (
          // Desktop Layout - Original 3 Columns (unchanged)
          <Grid container spacing={3}>
            {/* Left Column */}
            <Grid item xs={12} md={3}>
              <NetworkStatsCard stats={networkStatsData || {}} />
              <QuickActionsCard />
              <GrowthTipsCard tips={GROWTH_TIPS} />
            </Grid>

            {/* Middle Column */}
            <Grid item xs={12} md={6}>
              <Paper variant='outlined' sx={{ p: 3, mb: 2, border: 'none' }}>
                <form onSubmit={handleSearchSubmit}>
                  <TextField
                    fullWidth
                    placeholder="Search by name, company, or role..."
                    value={searchQuery}
                    onChange={handleSearch}
                    onKeyDown={handleKeyPress}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          {searchInitiated && (
                            <Button 
                              size="small" 
                              onClick={clearSearch}
                              sx={{ mr: 1 }}
                            >
                              Clear
                            </Button>
                          )}
                          <IconButton onClick={() => applyFilters()}>
                            <FilterIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </form>
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  <Chip
                    label={`Same Region ${showSameRegion ? '• On' : '• Off'}`}
                    variant="outlined"
                    onClick={() => {
                      setShowSameRegion(!showSameRegion);
                      setTimeout(applyFilters, 0);
                    }}
                    color={showSameRegion ? "primary" : "default"}
                  />
                  <Chip
                    label={`Trust Score ${selectedTrustScore !== 'all' ? `• ${selectedTrustScore}+` : '▼'}`}
                    variant="outlined"
                    onClick={handleTrustClick}
                    color={selectedTrustScore !== 'all' ? "primary" : "default"}
                  />
                  
                  <Popover
                    open={Boolean(anchorElTrust)}
                    anchorEl={anchorElTrust}
                    onClose={handleTrustClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                  >
                    <MenuList>
                      {TRUST_SCORE_RANGES.map((range) => (
                        <MenuItem 
                          key={range.value}
                          onClick={() => handleTrustSelect(range.value)}
                          selected={selectedTrustScore === range.value}
                        >
                          {range.label}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Popover>
                </Box>
              </Paper>

              {!searchInitiated && displayedUsers.length === 0 && !isSearchLoading ? (
                <EmptyBusinessNetworkState 
                  onSearchClick={() => {
                    const searchInput = document.querySelector('input[placeholder*="Search by name"]');
                    if (searchInput) {
                      searchInput.focus();
                    }
                  }}
                />
              ) : (
                <>
                  <Box sx={{ px: 2, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                    <Box>
                      <Typography variant="h6">
                        {searchInitiated ? 'Search Results' : 'Extended Business Network'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {searchInitiated 
                          ? `Found ${displayedUsers.length} results for "${searchQuery}"`
                          : 'Service providers from businesses in your extended network'}
                      </Typography>
                    </Box>
                    
                    {displayedUsers.length > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        Showing {page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, displayedUsers.length)} of {displayedUsers.length}
                      </Typography>
                    )}
                  </Box>
                  
                  {isSearchLoading ? (
                    [1, 2, 3].map((item) => (
                      <SearchResultSkeleton key={item} />
                    ))
                  ) : displayedUsers.length > 0 ? (
                    <>
                      {paginatedUsers.map((user) => (
                        <SearchResultCard
                          key={user._id}
                          thisUser={thisUser}
                          user={user}
                          onConnect={handleConnect}
                        />
                      ))}
                      
                      {displayedUsers.length > rowsPerPage && (
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'center', 
                          my: 3,
                          gap: 1
                        }}>
                          <Button 
                            variant="outlined" 
                            disabled={page === 0}
                            onClick={handlePrevPage}
                            size="small"
                          >
                            Previous
                          </Button>
                          
                          {[...Array(Math.min(5, Math.ceil(displayedUsers.length / rowsPerPage)))].map((_, i) => {
                            let pageNum;
                            const totalPages = Math.ceil(displayedUsers.length / rowsPerPage);
                            
                            if (totalPages <= 5) {
                              pageNum = i;
                            } else if (page < 3) {
                              pageNum = i;
                            } else if (page >= totalPages - 3) {
                              pageNum = totalPages - 5 + i;
                            } else {
                              pageNum = page - 2 + i;
                            }
                            
                            return (
                              <Button 
                                key={pageNum}
                                variant={page === pageNum ? "contained" : "outlined"}
                                onClick={() => handleChangePage(pageNum)}
                                size="small"
                                sx={{ minWidth: '40px' }}
                              >
                                {pageNum + 1}
                              </Button>
                            );
                          })}
                          
                          <Button 
                            variant="outlined" 
                            disabled={page >= Math.ceil(displayedUsers.length / rowsPerPage) - 1}
                            onClick={handleNextPage}
                            size="small"
                          >
                            Next
                          </Button>
                        </Box>
                      )}
                    </>
                  ) : (
                    <Paper variant='outlined' sx={{ p: 3, textAlign: 'center', border: 'none' }}>
                      <Typography variant="body1">No results found</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Try adjusting your search criteria
                      </Typography>
                    </Paper>
                  )}
                </>
              )}
            </Grid>

            {/* Right Column */}
            <Grid item xs={12} md={3}>
              <EmailInviteCard />
              <RecommendedUsersCard 
                users={suggestionsData}
                onConnect={handleConnect}
              />
            </Grid>
          </Grid>
        )}
      </Container>
    </Box>
  );
}